import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fetchNatalChart, NatalChartResponse } from '../../api';
import { cardStyle, CardDecorations, headingStyle, spinnerStyle, CollapsibleCard } from './dashboardStyles';

const DashboardNatalSummary: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [natalChart, setNatalChart] = useState<NatalChartResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchChart = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      try {
        const res = await fetchNatalChart(user.id);
        if (res.success && res.data) {
          setNatalChart(res.data);
        }
      } catch {
        // Silent fail — show CTA
      } finally {
        setLoading(false);
      }
    };
    fetchChart();
  }, [user?.id]);

  if (loading) {
    return (
      <div style={{ ...cardStyle, padding: isMobile ? '20px 16px' : '24px', marginBottom: '32px' }}>
        <CardDecorations />
        <div style={spinnerStyle}>✨ Загрузка натальной карты...</div>
      </div>
    );
  }

  if (!natalChart) {
    return (
      <div style={{ ...cardStyle, padding: isMobile ? '24px 20px' : '32px', marginBottom: '32px', textAlign: 'center' }}>
        <CardDecorations />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>🌟</div>
          <h2 style={{
            ...headingStyle(isMobile),
            marginBottom: '12px',
          }}>
            Натальная карта не рассчитана
          </h2>
          <p style={{ color: '#b0b0b0', fontSize: '14px', marginBottom: '20px' }}>
            Рассчитайте натальную карту, чтобы узнать свои планетарные позиции
          </p>
          <button
            onClick={() => navigate('/natal')}
            style={{
              background: 'linear-gradient(135deg, #d4af37, #b8942b)',
              color: '#1a1a2e',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 28px',
              fontSize: '15px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(212, 175, 55, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Рассчитать натальную карту
          </button>
        </div>
      </div>
    );
  }

  const bigThree = [
    {
      label: 'Солнце',
      symbol: '☀️',
      sign: natalChart.sunSignRu,
      degree: natalChart.planets.find(p => p.name === 'Sun')?.degreeFormatted || '',
      accent: '#d4af37',
      bgGlow: 'rgba(212, 175, 55, 0.1)',
    },
    {
      label: 'Луна',
      symbol: '🌙',
      sign: natalChart.moonSignRu,
      degree: natalChart.planets.find(p => p.name === 'Moon')?.degreeFormatted || '',
      accent: '#a0c4ff',
      bgGlow: 'rgba(160, 196, 255, 0.08)',
    },
    {
      label: 'Асцендент',
      symbol: '⬆️',
      sign: natalChart.ascendantRu,
      degree: natalChart.ascendantDegree || '',
      accent: '#c4a0ff',
      bgGlow: 'rgba(196, 160, 255, 0.08)',
    },
  ];

  return (
    <CollapsibleCard title="🌟 Ваша большая тройка" marginBottom="24px" padding={isMobile ? '16px 12px' : '20px'}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
        gap: '12px',
      }}>
        {bigThree.map((item) => (
          <div
            key={item.label}
            style={{
              background: item.bgGlow,
              border: `1px solid ${item.accent}33`,
              borderRadius: '16px',
              padding: '14px 12px',
              textAlign: 'center',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = `${item.accent}66`;
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = `${item.accent}33`;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div style={{ fontSize: '24px', marginBottom: '6px' }}>{item.symbol}</div>
            <div style={{ color: item.accent, fontSize: '12px', fontWeight: 600, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {item.label}
            </div>
            <div style={{ color: '#f0f0f0', fontSize: '15px', fontWeight: 600, marginBottom: '2px' }}>
              {item.sign}
            </div>
            <div style={{ color: '#888', fontSize: '12px' }}>
              {item.degree}
            </div>
          </div>
        ))}
      </div>
    </CollapsibleCard>
  );
};

export default DashboardNatalSummary;
